import React, { useState } from "react";
import { ChevronDown, ChevronRight, Search } from "lucide-react";

import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useCompanyEventRegistrations } from "../../hooks/useCompanyEventRegistrations";
import { CompanyEventRegistrationRow } from "./CompanyEventRegistrationRow";
import type { CompanyEventRegistrationItem } from "../../api/listCompanyEventRegistrations";

function Section({
  label,
  items,
  variant = "active",
  onCancelSuccess,
}: {
  label: string;
  items: CompanyEventRegistrationItem[];
  variant?: "active" | "past";
  onCancelSuccess: () => void;
}) {
  if (items.length === 0) return null;
  const sessionCount = items.length;
  const enrolleeCount = items.reduce((sum, i) => sum + i.enrollments.length, 0);
  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
          {label}
        </h2>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          {sessionCount} {sessionCount === 1 ? "session" : "sessions"} ·{" "}
          {enrolleeCount} {enrolleeCount === 1 ? "enrollee" : "enrollees"}
        </span>
      </div>
      <div className="space-y-3">
        {items.map((r) => (
          <CompanyEventRegistrationRow
            key={r.sessionUuid}
            onCancelSuccess={onCancelSuccess}
            registration={r}
            variant={variant}
          />
        ))}
      </div>
    </section>
  );
}

function CollapsibleSection({
  label,
  items,
  onCancelSuccess,
}: {
  label: string;
  items: CompanyEventRegistrationItem[];
  onCancelSuccess: () => void;
}) {
  const [open, setOpen] = useState(false);
  if (items.length === 0) return null;
  const sessionCount = items.length;
  return (
    <section className="space-y-3">
      <button
        aria-expanded={open}
        className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        onClick={() => setOpen((o) => !o)}
        type="button"
      >
        {open ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        {label}
        <span className="text-xs text-gray-400 dark:text-gray-500 normal-case tracking-normal">
          ({sessionCount})
        </span>
      </button>
      {open && (
        <div className="space-y-3">
          {items.map((r) => (
            <CompanyEventRegistrationRow
              key={r.sessionUuid}
              onCancelSuccess={onCancelSuccess}
              registration={r}
              variant="past"
            />
          ))}
        </div>
      )}
    </section>
  );
}

const CompanyEventRegistrationsPage: React.FC = () => {
  const {
    groups,
    upcomingCount,
    pastCount,
    eventOptions,
    filters,
    setFilters,
    loading,
    error,
    refetch,
  } = useCompanyEventRegistrations();

  return (
    <MainPageWrapper
      loading={loading}
      subtitle="Every event registration across your company."
      title="Company Event Registrations"
    >
      {/* Filter strip */}
      <div className="mb-6 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            className="pl-9"
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Search employee, event, or session..."
            value={filters.search}
          />
        </div>
        <Select
          onValueChange={(v) =>
            setFilters({ ...filters, eventUuid: v === "__all__" ? "" : v })
          }
          value={filters.eventUuid || "__all__"}
        >
          <SelectTrigger className="sm:w-56">
            <SelectValue placeholder="All events" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All events</SelectItem>
            {eventOptions.map((opt) => (
              <SelectItem key={opt.uuid} value={opt.uuid}>
                {opt.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          onValueChange={(v) =>
            setFilters({
              ...filters,
              status: v as "all" | "active" | "cancelled",
            })
          }
          value={filters.status}
        >
          <SelectTrigger className="sm:w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active only</SelectItem>
            <SelectItem value="cancelled">Cancelled / Refunded</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {error && (
        <div className="py-16 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
        </div>
      )}

      {!error && !loading && (
        <>
          {upcomingCount === 0 && pastCount === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 dark:border-slate-700 px-6 py-16 text-center">
              <h2 className="text-base font-normal text-gray-900 dark:text-white">
                No registrations match these filters
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Try clearing the search or status filter.
              </p>
            </div>
          ) : (
            <div className="space-y-10">
              <Section
                items={groups.thisWeek}
                label="This week"
                onCancelSuccess={refetch}
              />
              <Section
                items={groups.laterThisMonth}
                label="Later this month"
                onCancelSuccess={refetch}
              />
              <Section
                items={groups.future}
                label="Future"
                onCancelSuccess={refetch}
              />
              {upcomingCount === 0 && (
                <div className="rounded-lg border border-dashed border-gray-300 dark:border-slate-700 px-6 py-10 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No upcoming registrations in this filter.
                  </p>
                </div>
              )}
              <CollapsibleSection
                items={groups.past}
                label="Past"
                onCancelSuccess={refetch}
              />
            </div>
          )}
        </>
      )}
    </MainPageWrapper>
  );
};

export default CompanyEventRegistrationsPage;
